import React, { useState, useEffect } from 'react';
import { translations } from '../translations';
import apiService from '../services/apiService';

const QoR15Questionnaire = ({ patientData, taskId, language = 'en', onComplete, onClose }) => {
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const t = translations[language] || translations.en;

  // Charger les réponses existantes si elles existent
  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingResponses = JSON.parse(response.data.responses);
          setResponses(existingResponses);
          setIsEditing(true);
        }
      } catch (error) {
        console.error('Erreur chargement réponses existantes:', error);
      }
    };

    loadExistingResponses();
  }, [patientData, taskId]);

  // Questions du questionnaire QoR-15 (Quality of Recovery-15) - Version officielle allemande
  const questions = {
    en: [
      {
        id: 'q1',
        question: 'Able to breathe easily',
        reverseScored: false
      },
      {
        id: 'q2',
        question: 'Have had a good sleep',
        reverseScored: false
      },
      {
        id: 'q3',
        question: 'Been able to enjoy food',
        reverseScored: false
      },
      {
        id: 'q4',
        question: 'Feeling rested',
        reverseScored: false
      },
      {
        id: 'q5',
        question: 'Able to look after personal toilet and hygiene unaided',
        reverseScored: false
      },
      {
        id: 'q6',
        question: 'Able to communicate with family or friends',
        reverseScored: false
      },
      {
        id: 'q7',
        question: 'Getting support from hospital doctors and nurses',
        reverseScored: false
      },
      {
        id: 'q8',
        question: 'Able to return to work or usual home activities',
        reverseScored: false
      },
      {
        id: 'q9',
        question: 'Moderate pain',
        reverseScored: true
      },
      {
        id: 'q10',
        question: 'Severe pain',
        reverseScored: true
      },
      {
        id: 'q11',
        question: 'Nausea',
        reverseScored: true
      },
      {
        id: 'q12',
        question: 'Vomiting',
        reverseScored: true
      },
      {
        id: 'q13',
        question: 'Feeling worried or anxious',
        reverseScored: true
      },
      {
        id: 'q14',
        question: 'Feeling sad or depressed',
        reverseScored: true
      },
      {
        id: 'q15',
        question: 'Feeling angry or irritable',
        reverseScored: true
      }
    ],
    de: [
      {
        id: 'q1',
        question: 'Konnten Sie frei und problemlos atmen?',
        reverseScored: false
      },
      {
        id: 'q2',
        question: 'Haben Sie gut geschlafen?',
        reverseScored: false
      },
      {
        id: 'q3',
        question: 'Konnten Sie das Essen genießen?',
        reverseScored: false
      },
      {
        id: 'q4',
        question: 'Fühlten Sie sich ausgeruht?',
        reverseScored: false
      },
      {
        id: 'q5',
        question: 'Konnten Sie sich eigenständig um Ihre Körperpflege/Toilette kümmern?',
        reverseScored: false
      },
      {
        id: 'q6',
        question: 'Konnten Sie mit Familie oder Freunden kommunizieren?',
        reverseScored: false
      },
      {
        id: 'q7',
        question: 'Haben Sie Unterstützung durch das Krankenhauspersonal erhalten?',
        reverseScored: false
      },
      {
        id: 'q8',
        question: 'Waren Sie in der Lage, zu Ihrer Arbeit oder Ihren üblichen häuslichen Aktivitäten zurückzukehren?',
        reverseScored: false
      },
      {
        id: 'q9',
        question: 'Hatten Sie mäßige Schmerzen?',
        reverseScored: true
      },
      {
        id: 'q10',
        question: 'Hatten Sie starke Schmerzen?',
        reverseScored: true
      },
      {
        id: 'q11',
        question: 'Hatten Sie Übelkeit?',
        reverseScored: true
      },
      {
        id: 'q12',
        question: 'Mussten Sie sich übergeben (Erbrechen)?',
        reverseScored: true
      },
      {
        id: 'q13',
        question: 'Fühlten Sie sich ängstlich oder besorgt?',
        reverseScored: true
      },
      {
        id: 'q14',
        question: 'Fühlten Sie sich traurig oder niedergeschlagen?',
        reverseScored: true
      },
      {
        id: 'q15',
        question: 'Fühlten Sie sich wütend oder gereizt?',
        reverseScored: true
      }
    ]
  };

  const currentQuestions = questions[language] || questions.en;

  // Vérifier si toutes les questions ont été répondues
  const isComplete = currentQuestions.every(q => {
    const response = responses[q.id];
    if (!response) return false;
    
    if (typeof response === 'object') {
      return response.value !== undefined && response.value >= 0;
    } else {
      return response !== undefined && response !== '' && response >= 0;
    }
  });

  // Calculer le score QoR-15 (0-150, où 150 = meilleur)
  const calculateScore = () => {
    const total = currentQuestions.reduce((sum, q) => {
      const response = responses[q.id];
      const value = response ? (typeof response === 'object' ? response.value : response) : 0;
      
      if (q.reverseScored) {
        // Pour les questions inversées : 0=meilleur, 10=pire
        return sum + (10 - value);
      } else {
        // Pour les questions normales : 0=pire, 10=meilleur
        return sum + value;
      }
    }, 0);
    return total; // Score final 0-150
  };

  // Fonction pour obtenir le label d'une option
  const getOptionLabel = (questionId, value) => {
    // Pour QoR-15, les options sont de 0 à 10
    const labels = {
      en: {
        0: '0 - Never/Very poor',
        1: '1',
        2: '2',
        3: '3',
        4: '4',
        5: '5',
        6: '6',
        7: '7',
        8: '8',
        9: '9',
        10: '10 - Always/Very good'
      },
      de: {
        0: '0 - Nie/Sehr schlecht',
        1: '1',
        2: '2',
        3: '3',
        4: '4',
        5: '5',
        6: '6',
        7: '7',
        8: '8',
        9: '9',
        10: '10 - Immer/Sehr gut'
      }
    };

    const languageLabels = labels[language] || labels.en;
    return languageLabels[value] || value.toString();
  };

  const handleResponseChange = (questionId, value) => {
    // Permettre les valeurs vides pour permettre la saisie
    if (value === '') {
      setResponses(prev => ({
        ...prev,
        [questionId]: undefined
      }));
      return;
    }
    
    // Valider que la valeur est entre 0 et 10
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 10) {
      return;
    }
    
    const label = getOptionLabel(questionId, numValue);
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        value: numValue,
        label: label
      }
    }));
  };

  const handleSubmit = async () => {
    if (!isComplete) return;

    setLoading(true);
    setError(null);

    try {
      const score = calculateScore();
      
      if (isEditing) {
        // Mode édition : utiliser la route de mise à jour
        await apiService.updateQuestionnaireResponse(
          patientData.patientId,
          taskId,
          'QoR15',
          JSON.stringify(responses),
          score
        );
      } else {
        // Mode création : utiliser la route de soumission
        const responseData = {
          patientId: patientData.patientId,
          taskId: taskId,
          questionnaireType: 'QoR15',
          responses: JSON.stringify(responses),
          score: score,
          language: language,
          submissionDate: (() => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
          })()
        };
        await apiService.submitQuestionnaire(responseData);
      }
      
      if (onComplete) {
        onComplete(score, responses);
      }
    } catch (error) {
      console.error('Erreur soumission questionnaire:', error);
      setError('Erreur lors de la soumission du questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const getScoreCategory = (score) => {
    if (score >= 120) return { category: 'Excellent', color: '#10b981' };
    if (score >= 90) return { category: 'Good', color: '#3b82f6' };
    if (score >= 60) return { category: 'Moderate', color: '#f59e0b' };
    return { category: 'Poor', color: '#ef4444' };
  };

  const scoreCategory = getScoreCategory(calculateScore());

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content">
        <div className="questionnaire-header">
          <h2 className="questionnaire-title">
            {language === 'de' 
              ? `QoR-15 – Fragebogen zur Qualität der Erholung nach einer Operation ${isEditing ? '(Bearbeiten)' : ''}`
              : `QoR-15 – Quality of Recovery Questionnaire ${isEditing ? '(Edit)' : ''}`
            }
          </h2>
          <button className="questionnaire-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="questionnaire-instructions">
          <p>
            {language === 'de' 
              ? 'Anleitung: Bitte beantworten Sie die folgenden Fragen danach, wie Sie sich in den letzten 24 Stunden gefühlt haben. Antwortskala: 0 = nie / sehr schlecht, 10 = immer / sehr gut. Je höher der Wert, desto besser die Erholung.'
              : 'How have you been feeling in the last 24 hours? (0 to 10, where: 0 = none of the time [poor] and 10 = all of the time [excellent])'
            }
          </p>
        </div>

        <div className="questions-list">
          {currentQuestions.map((question, index) => (
            <div key={question.id} className={`question-item ${question.reverseScored ? 'reverse-scored' : ''}`}>
                              <h3 className="question-title">
                  {index + 1}. {question.question}
                </h3>
              <div className="question-input-container">
                <label className="input-label">
                  {language === 'de' ? 'Wert (0-10):' : 'Value (0-10):'}
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={responses[question.id] ? (typeof responses[question.id] === 'object' ? responses[question.id].value : responses[question.id]) : ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    className="qor15-input"
                    placeholder="0-10"
                  />
                </label>
                <div className="scale-labels">
                  {question.reverseScored ? (
                    <>
                      <span className="scale-min">
                        {language === 'de' ? '0 = nie (beste)' : '0 = all of the time [poor]'}
                      </span>
                      <span className="scale-max">
                        {language === 'de' ? '10 = immer (schlimmste)' : '10 = none of the time [excellent]'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="scale-min">
                        {language === 'de' ? '0 = nie/sehr schlecht' : '0 = none of the time [poor]'}
                      </span>
                      <span className="scale-max">
                        {language === 'de' ? '10 = immer/sehr gut' : '10 = all of the time [excellent]'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="questionnaire-footer">
          <button
            className={`submit-btn ${!isComplete || loading ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!isComplete || loading}
          >
            {loading 
              ? (language === 'de' ? 'Wird gesendet...' : 'Submitting...')
              : (language === 'de' ? 'Absenden' : 'Submit')
            }
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default QoR15Questionnaire; 